"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReportSection = void 0;
const ui_1 = require("@app/component/ui");
const data_grid_1 = require("@app/component/ui/data-grid");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const input_label_1 = require("@app/component/ui/forms/input-label/input-label");
const InfoBoxV2_1 = require("@app/component/ui/info-box/InfoBoxV2");
const const_1 = require("@app/const");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const format_hours_1 = require("@app/util/format-hours");
const forms_1 = require("@app/util/forms/forms");
const use_go_to_detailed_users_report_1 = require("@app/util/hooks/use-go-to-detailed-users-report");
const useSortedContracts_1 = require("@app/util/hooks/useSortedContracts");
const use_modal_1 = require("@app/util/modal/use-modal");
const safe_unwrap_or_null_1 = require("@app/util/type-safety/safe-unwrap-or-null");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const employee_stats_1 = require("../../request/employee-stats");
const employee_card_context_1 = require("../../util/employee-card-context");
const useEmployeeCardFooterContent_1 = require("../../util/hooks/useEmployeeCardFooterContent");
const useUserDetailsContracts_1 = require("../../util/hooks/useUserDetailsContracts");
const useUserDetailsStats_1 = require("../../util/hooks/useUserDetailsStats");
const filterByContractType = (contractType) => (e) => e?.contractType === contractType;
const sortTimeOffs = (timeOffs) => (0, util_1.isDefined)(timeOffs)
    ? Array.from(timeOffs).sort((a, b) => {
        return a.minutes > b.minutes ? -1 : b.minutes > a.minutes ? 1 : 0;
    })
    : [];
const getEmployeeTimeOffCell = (timeOff) => {
    const displayAsDay = timeOff?.category?.displayWorkTimeAsDays ?? false;
    return {
        label: timeOff?.category.title ?? '',
        value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(timeOff?.minutes, (m) => displayAsDay
            ? (0, util_1.t)('DAYS_X', { x: util_1.numberUtil.round2decimals(m / 60 / 8) })
            : (0, format_hours_1.formatHours)(m / 60)),
    };
};
const ReportSection = () => {
    const { workspace, employee } = (0, employee_card_context_1.useEmployeeCard)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const { canRead } = (0, util_1.useAccessRole)();
    const contracts = (0, useUserDetailsContracts_1.useUserDetailsContracts)();
    const { activeContracts } = (0, useSortedContracts_1.useSortedContracts)(contracts);
    const wsHasBalancingPeriod = workspace?.settings?.balancingPeriod;
    const periodOptions = [
        {
            label: (0, util_1.t)('MONTH'),
            value: 'month',
        },
        {
            label: (0, util_1.t)('AGREEMENT'),
            value: 'contract',
        },
        wsHasBalancingPeriod
            ? {
                label: (0, util_1.t)('CURRENT_BALANCING_PERIOD'),
                value: 'current',
            }
            : undefined,
        {
            label: (0, util_1.t)('YEAR'),
            value: 'year',
        },
    ].filter(Boolean);
    const contractTypes = workspace?.contractTypes?.map((c) => ({
        id: c.id,
        name: c.name,
        fixed: c.rules.fixedWorkHours,
    }));
    const contractOptions = react_1.default.useMemo(() => contracts?.map((c) => {
        const ct = contractTypes?.find((ct) => ct.id === c.type);
        const name = (0, maybe_unwrap_1.unwrapMaybe)(ct?.name) ?? c.type ?? '-';
        const start = (0, moment_1.default)(c.period?.start).format('DD.MM.YYYY');
        const end = c?.period?.end
            ? (0, util_1.t)('{{value, date_formatted}}', { value: c?.period?.end })
            : (0, util_1.t)('EMPLOYEE_PROFILE_CONT_DATE_UNDEFINED');
        return {
            label: `${name} ${start} - ${end}`,
            value: (0, maybe_unwrap_1.unwrapMaybe)(c.id),
        };
    }) ?? [], [contractTypes, contracts]);
    const { control, setValue } = (0, react_hook_form_1.useForm)({
        defaultValues: {
            period: 'month',
            month: (0, moment_1.default)().format('YYYY-MM'),
            year: (0, moment_1.default)().format('YYYY'),
        },
    });
    const injectValue = (0, forms_1.useInjectValue)(setValue);
    const { field: monthController } = (0, react_hook_form_1.useController)({ control, name: 'month' });
    const { field: yearController } = (0, react_hook_form_1.useController)({ control, name: 'year' });
    const selectedPeriod = (0, react_hook_form_1.useWatch)({
        control,
        name: 'period',
    });
    const selectedMonth = (0, react_hook_form_1.useWatch)({
        control,
        name: 'month',
    });
    const selectedYear = (0, react_hook_form_1.useWatch)({
        control,
        name: 'year',
    });
    const selectedContract = (0, react_hook_form_1.useWatch)({
        control,
        name: 'contractId',
    });
    react_1.default.useEffect(() => {
        if ((0, util_1.isUndefined)(activeContracts))
            return;
        const first = activeContracts.at(0);
        if ((0, util_1.isUndefined)(first))
            return;
        if ((0, util_1.isDefined)(selectedContract))
            return;
        injectValue('contractId', (0, maybe_unwrap_1.unwrapMaybe)(first.id));
    }, [activeContracts, selectedContract]);
    const statsPeriod = react_1.default.useMemo(() => {
        switch (selectedPeriod) {
            case 'month':
                return {
                    start: (0, moment_1.default)(selectedMonth).startOf('month').toDate(),
                    end: (0, moment_1.default)(selectedMonth).endOf('month').toDate(),
                };
            case 'year':
                return {
                    start: (0, moment_1.default)(selectedYear).startOf('year').toDate(),
                    end: (0, moment_1.default)(selectedYear).endOf('year').toDate(),
                };
            case 'current':
                // compute the current balancing period start & end, with respect to today's date
                return (util_1.timeUtil.getCurrentBalancingPeriod(workspace?.settings?.balancingPeriod) || {
                    start: (0, moment_1.default)(selectedMonth).startOf('month').toDate(),
                    end: (0, moment_1.default)(selectedMonth).endOf('month').toDate(),
                });
            case 'contract': {
                const currentContract = contracts?.find((c) => c.id === selectedContract);
                const period = (0, maybe_unwrap_1.unwrapMaybe)(currentContract?.period);
                // Employee doesn't have a contract -> show error and default
                if ((0, util_1.isUndefined)(currentContract) || (0, util_1.isUndefined)(period)) {
                    util_1.notification.error({
                        message: (0, util_1.t)('WARNING_DESC_employee_outOfContract'),
                    });
                    setValue('period', 'month');
                    return {
                        start: (0, moment_1.default)().toDate(),
                        end: (0, moment_1.default)().toDate(),
                    };
                }
                // Employee has an active contract -> show stats from start of the contract until today
                if ((0, util_1.isUndefined)(period.end) || (0, util_1.isNull)(period.end)) {
                    return {
                        start: (0, moment_1.default)(period.start).toDate(),
                        end: (0, moment_1.default)().toDate(),
                    };
                }
                // Employee has a contract and it has ended already
                return {
                    start: (0, moment_1.default)(period.start).toDate(),
                    end: (0, moment_1.default)(period.end).toDate(),
                };
            }
        }
    }, [selectedPeriod, selectedMonth, selectedYear, selectedContract]);
    const splitByContractType = selectedPeriod === 'contract';
    const roleStats = (0, useUserDetailsStats_1.useUserDetailsStats)(statsPeriod.end, splitByContractType);
    const contractTypeCurrent = contracts?.find((cts) => cts.id === selectedContract)?.type || '';
    const FilterByContractType = filterByContractType((0, maybe_unwrap_1.unwrapMaybe)(contractTypeCurrent));
    const contractStats = {
        balancingPeriodContractMinutesByContract: roleStats?.balancingPeriodContractMinutesByContract?.find(FilterByContractType),
        balancingPeriodPlannedMinutesByContract: roleStats?.balancingPeriodPlannedMinutesByContract?.find(FilterByContractType),
        monthContractMinutesByContract: roleStats?.monthContractMinutesByContract?.find(FilterByContractType),
        monthPlannedMinutesByContract: roleStats?.monthPlannedMinutesByContract?.find(FilterByContractType),
        yearVacationByContracts: roleStats?.yearVacationByContracts?.find(FilterByContractType),
    };
    const [employeeStats, setEmployeeStats] = react_1.default.useState();
    react_1.default.useEffect(() => {
        if ((0, util_1.isUndefined)(statsPeriod)) {
            throw new Error('Stats period should not be undefined'); // Something bad has happened
        }
        const action = async () => {
            const _employeeStats = await (0, employee_stats_1.requestEmployeeStats)({
                workspaceId: workspace?.id ?? '',
                userId: employee.user?.id ?? '',
                period: statsPeriod,
            });
            if (!(0, util_1.isValue)(_employeeStats))
                return;
            const timeOffs = structuredClone(_employeeStats.timeOffs);
            const sortedTimeOffs = sortTimeOffs(timeOffs);
            _employeeStats.timeOffs = sortedTimeOffs;
            setEmployeeStats(_employeeStats);
        };
        void action();
    }, [statsPeriod]);
    const { displayContent } = (0, useEmployeeCardFooterContent_1.useEmployeeCardFooterContent)(react_1.default.createElement("div", { style: { display: 'flex' } },
        react_1.default.createElement(ui_1.Button, { onClick: () => {
                closeModal();
                // void dispatch(setModal(null))
            } }, (0, util_1.t)('CLOSE')),
        react_1.default.createElement(ui_1.Button, { style: 'CONTAINED', disabled: true }, (0, util_1.t)('SAVE'))));
    const filterPicker = () => {
        if (selectedPeriod === 'month') {
            return (react_1.default.createElement("div", { style: { width: '100%' } },
                react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('MONTH') }),
                react_1.default.createElement(ui_1.MonthSelect, { returnType: ui_1.MonthSelect.RETURN_TYPES.STRING, style: ui_1.MonthSelect.STYLES.DEFAULT, value: monthController.value, onChange: monthController.onChange, forbiddenFuture: true })));
        }
        if (selectedPeriod === 'year') {
            return (react_1.default.createElement("div", null,
                react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('YEAR') }),
                react_1.default.createElement(ui_1.YearSelect, { fullWidth: true, value: (0, moment_1.default)(yearController.value, 'YYYY'), onChange: (year) => {
                        yearController.onChange((0, moment_1.default)(year).format('YYYY'));
                    }, forbiddenFuture: true })));
        }
        if (selectedPeriod === 'contract') {
            return (react_1.default.createElement("div", null,
                react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('EMPLOYEE_PROFILE_CONTRACT_TYPE') }),
                react_1.default.createElement(dropdown_input_1.DropdownInput, { name: 'contractId', control: control, options: contractOptions })));
        }
        if (selectedPeriod === 'current') {
            return null;
        }
        return null;
    };
    const goToDetailedReport = (0, use_go_to_detailed_users_report_1.useGoToDetailedUsersReport)(selectedMonth ?? '');
    const sortedTimeOffs = sortTimeOffs(employeeStats?.timeOffs ?? []);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL },
            react_1.default.createElement("div", { style: { display: 'grid', gridTemplateColumns: '50%', width: '100%' } },
                react_1.default.createElement("div", { style: {
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem',
                    } },
                    react_1.default.createElement("div", { style: { width: '100%' } },
                        react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('PERIOD') }),
                        react_1.default.createElement(dropdown_input_1.DropdownInput, { name: 'period', control: control, options: periodOptions })),
                    react_1.default.createElement("div", { style: { width: '100%' } }, filterPicker())))),
        react_1.default.createElement(ui_1.Text, { type: 'header', weight: 'bold' }, (0, util_1.t)('DASHBOARD')),
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8, type: ui_1.Spacing.TYPES.VERTICAL }),
        react_1.default.createElement("div", { style: { display: 'grid', gridTemplateColumns: '50%', width: '100%' } },
            react_1.default.createElement(InfoBoxV2_1.InfoBoxV2, { size: 'full-width', header: react_1.default.createElement(react_1.default.Fragment, null, canRead(const_1.PERMISSIONS.BACKOFFICE.REPORT_EMPLOYEE) ? (react_1.default.createElement("div", { style: { width: '100%', display: 'flex', justifyContent: 'end' } },
                    react_1.default.createElement(ui_1.Flex, { justify: 'space-between' },
                        react_1.default.createElement("div", { onClick: () => goToDetailedReport([employee.user?.id ?? '']), style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                            } },
                            react_1.default.createElement("div", { style: { marginRight: '2rem' } },
                                react_1.default.createElement("a", { style: { color: '#0045ff' }, type: 'button' }, (0, util_1.t)('DETAILED_REPORT'))),
                            react_1.default.createElement("span", null,
                                react_1.default.createElement("a", { style: { color: '#0045ff' }, type: 'button' }, '\u203a')))))) : null), grow: 1, basis: 40, cols: [
                    {
                        data: [
                            {
                                label: (0, util_1.t)('ATTENDANCE'),
                                value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.workMinutes, (wm) => wm / 60),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                            },
                            {
                                label: (0, util_1.t)('FOND'),
                                value: selectedPeriod === 'contract'
                                    ? (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(contractStats?.monthContractMinutesByContract?.minutes, (minutes) => minutes / 60)
                                    : selectedPeriod === 'current'
                                        ? (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(roleStats?.balancingPeriodContractMinutes, (minutes) => minutes / 60)
                                        : (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(roleStats?.monthContractMinutes, (minutes) => minutes / 60),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                            },
                            {
                                label: (0, util_1.t)('OVERTIME'),
                                value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.overTimeMinutes, (ot) => `(+${(0, format_hours_1.formatHours)(ot / 60)})`),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.TEXT,
                                helpText: (0, util_1.t)('ATTEN_WIDG_HELP_OVERTIME'),
                            },
                            {
                                label: (0, util_1.t)('DIFFERENCE'),
                                value: ((employeeStats?.workMinutes ?? 0) -
                                    (roleStats?.monthContractMinutes ?? 0)) /
                                    60,
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                fieldType: InfoBoxV2_1.InfoBoxV2.FIELD_TYPES.DIFFERENCE,
                            },
                        ],
                    },
                    {
                        data: [
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            {
                                label: (0, util_1.t)('ATTEN_WIDG_HOURS'),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.workMinutes, (minutes) => minutes / 60),
                            },
                            {
                                label: (0, util_1.t)('ATTEN_WIDG_UNAVS'),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(sortedTimeOffs?.reduce((acc, prev) => {
                                    return acc + prev.minutes;
                                }, 0), (minutes) => minutes / 60),
                            },
                        ],
                    },
                ] })),
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8, type: ui_1.Spacing.TYPES.VERTICAL }),
        react_1.default.createElement(data_grid_1.DataGrid, { columns: [
                {
                    cells: [
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.basic'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.workDayMinutes, (wdm) => (0, format_hours_1.formatHours)(wdm / 60)),
                        },
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.saturdays'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.saturdayMinutes, (sm) => (0, format_hours_1.formatHours)(sm / 60)),
                        },
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.sundays'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.sundayMinutes, (sm) => (0, format_hours_1.formatHours)(sm / 60)),
                        },
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.nights'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.nightMinutes, (nm) => (0, format_hours_1.formatHours)(nm / 60)),
                        },
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.holidays'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.holidayMinutes, (hm) => (0, format_hours_1.formatHours)(hm / 60)),
                        },
                    ],
                },
                {
                    cells: [
                        getEmployeeTimeOffCell(employeeStats?.timeOffs?.[0]),
                        getEmployeeTimeOffCell(employeeStats?.timeOffs?.[1]),
                        getEmployeeTimeOffCell(employeeStats?.timeOffs?.[2]),
                        getEmployeeTimeOffCell(employeeStats?.timeOffs?.[3]),
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS_LABEL.weekly_average'),
                            value: (0, safe_unwrap_or_null_1.safeUnwrapOrNull)(employeeStats?.workMinutesWeekAverage, (wmwa) => (0, format_hours_1.formatHours)(wmwa / 60)),
                        },
                    ],
                },
            ] }),
        displayContent()));
};
exports.ReportSection = ReportSection;
